"use client";
import { useRouter, usePathname } from 'next/navigation';
import { useEffect, ReactNode } from 'react';
import { useAuth } from '@/context/AuthContext';

interface ProtectedProps {
  children: ReactNode;
}

const Protected = ({ children }: ProtectedProps) => {
  const { user, loading } = useAuth();
  const router = useRouter();
  const pathname = usePathname();

  // Always validate auth on mount and on every route change
  useEffect(() => {
    if (!loading) {
      if (!user) {
        // Prefetch login for faster navigation then replace history
        router.prefetch?.('/login');
        router.replace('/login');
      }
    }
  }, [user, loading, router, pathname]);

  // Polished full-screen loader while we check auth OR when redirecting
  if (loading || !user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6">
        <div className="relative">
          <div className="h-16 w-16 rounded-full border-4 border-gray-200/30 animate-spin border-t-transparent" />
          <div className="absolute inset-0 h-16 w-16 rounded-full border-4 border-indigo-500/70 animate-[spin_1.2s_linear_infinite] border-t-transparent" />
        </div>
        <div className="text-center">
          <p className="text-sm text-gray-400">Checking your session…</p>
          <p className="text-xs text-gray-500">Redirecting to login if not authenticated</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default Protected;
